import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { TableCell } from '@anytag/shared-atoms';
import { usePostDetailsParam } from '@anytag/shared-hooks';
import { convertDurationToHhMmSs, formatIntNumber, formatPercent } from '@anytag/shared-utils';
import { YoutubeAnalyticsPostsPayload, YoutubeAnalyticsPostsSortOrder } from '@anytag/graphql';

export const useInfluencerYoutubeSponsoredPostsListTable = () => {
  const { t } = useTranslation();
  const { setPostDetailsParam } = usePostDetailsParam();

  const columns: ColumnDef<YoutubeAnalyticsPostsPayload>[] = [
    {
      accessorKey: 'post',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Videos'),
      cell: ({ row }) => (
        <TableCell.Post
          size="44px"
          title={row.original.videoTitle}
          thumbnail={row.original.videoThumbnail || ''}
          onClick={() => setPostDetailsParam(row.original.id)}
        />
      )
    },
    {
      accessorKey: 'views',
      header: t<string>('HeaderColumn.View'),
      meta: { align: 'right', sortField: YoutubeAnalyticsPostsSortOrder.VIEWS },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.views)} />
    },
    {
      accessorKey: 'duration',
      header: t<string>('HeaderColumn.Play Time'),
      meta: { align: 'right', sortField: YoutubeAnalyticsPostsSortOrder.DURATION },
      cell: ({ row }) => <TableCell.Number value={convertDurationToHhMmSs(row.original.videoDuration)} />
    },
    {
      accessorKey: 'averageViewDuration',
      header: t<string>('HeaderColumn.Average View Duration'),
      meta: { align: 'right', sortField: YoutubeAnalyticsPostsSortOrder.AVERAGE_VIEW_DURATION },
      cell: ({ row }) => <TableCell.Number value={convertDurationToHhMmSs(row.original.averageViewDuration ?? null)} />
    },
    {
      accessorKey: 'averageViewPercentage',
      header: t<string>('HeaderColumn.Average View Duration Rate'),
      meta: { align: 'right', sortField: YoutubeAnalyticsPostsSortOrder.AVERAGE_VIEW_PERCENTAGE },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.averageViewPercentage, false)} unit="%" />
    },
    {
      accessorKey: 'likes',
      header: t<string>('HeaderColumn.Like'),
      meta: { align: 'right', sortField: YoutubeAnalyticsPostsSortOrder.LIKE },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.likes)} />
    },
    {
      accessorKey: 'dislikes',
      header: t<string>('HeaderColumn.Dislike'),
      meta: { align: 'right', sortField: YoutubeAnalyticsPostsSortOrder.DISLIKE },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.dislikes)} />
    },
    {
      accessorKey: 'comments',
      header: t<string>('HeaderColumn.Comment'),
      meta: { align: 'right', sortField: YoutubeAnalyticsPostsSortOrder.COMMENT },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.comments)} />
    },
    {
      accessorKey: 'shares',
      header: t<string>('HeaderColumn.Share'),
      meta: { align: 'right', sortField: YoutubeAnalyticsPostsSortOrder.SHARE },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.shares)} />
    },
    {
      accessorKey: 'postDate',
      header: t<string>('HeaderColumn.Post date'),
      meta: { sortField: YoutubeAnalyticsPostsSortOrder.POST_DATE },
      cell: ({ row }) => <TableCell.Date className="min-w-20" value={row.original.postDate} />
    }
  ];

  return { columns };
};
